import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/90 backdrop-blur-xl shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">

          {/* Left - Brand */}
          <Link to="/" className="flex items-center gap-1.5 group">
            <span className="text-base md:text-lg font-semibold text-foreground-900 tracking-tight">
              {t('nav.brand')}
            </span>
            <span className="text-xs text-foreground-500 font-normal -mt-0.5">
              {t('nav.brand_sub')}
            </span>
          </Link>

          {/* Center - Nav links */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('nav.home')}
            </Link>
            <span className="text-foreground-300 text-xs">◆</span>
            <Link
              to="/article/oopbuy-spreadsheet-einkaufsfuehrer"
              className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('nav.shopping_guide')}
            </Link>
            <span className="text-foreground-300 text-xs">◆</span>
            <Link
              to="/oopbuy-spreadsheet-guide"
              className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              Guide
            </Link>
            <span className="text-foreground-300 text-xs">◆</span>
            <span className="text-sm text-foreground-400 whitespace-nowrap">FAQ</span>
          </div>

          {/* Right - Dropdown + CTAs */}
          <div className="flex items-center gap-3">
            {/* Category Dropdown - Desktop only */}
            <div className="relative hidden md:block" ref={dropdownRef}>
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-50 border border-background-300 text-sm text-foreground-700 hover:border-foreground-300 transition-all whitespace-nowrap cursor-pointer"
              >
                <span className="max-w-[120px] truncate">{t('nav.dropdown_placeholder')}</span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isOpen && (
                <div className="absolute right-0 mt-2 w-60 bg-background-50 rounded-xl shadow-lg border border-background-200 py-2 max-h-[420px] overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setIsOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 text-xs font-bold flex-shrink-0">
                        {cat.id.toString().slice(-2)}
                      </span>
                      {t(cat.nameKey)}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* CTA Buttons */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:flex items-center gap-1.5 px-5 py-2 rounded-full bg-foreground-900 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
            >
              {t('nav.cta_spreadsheet')}
              <i className="ri-arrow-right-up-line text-base"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:flex items-center gap-1.5 px-5 py-2 rounded-full bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer"
            >
              {t('nav.cta_buy')}
              <i className="ri-arrow-right-up-line text-base"></i>
            </a>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden flex items-center justify-center w-10 h-10 rounded-full bg-background-50 border border-background-300 hover:bg-background-100 transition-all cursor-pointer shadow-sm"
              aria-label="Menü öffnen"
            >
              <i className={`text-xl text-foreground-800 ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-4-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile overlay menu */}
        {mobileMenuOpen && (
          <div className="md:hidden fixed inset-x-0 top-16 bottom-0 bg-background-50 z-40 overflow-y-auto">
            <div className="px-5 pt-2 pb-8">

              {/* Main nav links */}
              <div className="flex flex-col gap-1 mb-6">
                <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-3.5 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-xl transition-colors cursor-pointer">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-home-4-line text-base"></i>
                  </span>
                  {t('nav.home')}
                </Link>
                <Link to="/article/oopbuy-spreadsheet-einkaufsfuehrer" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-3.5 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-xl transition-colors cursor-pointer">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-shopping-bag-3-line text-base"></i>
                  </span>
                  {t('nav.shopping_guide')}
                </Link>
                <Link to="/oopbuy-spreadsheet-guide" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-3.5 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-xl transition-colors cursor-pointer">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                    <i className="ri-book-open-line text-base"></i>
                  </span>
                  Guide
                </Link>
              </div>

              {/* Divider */}
              <div className="h-px bg-background-200 mb-5"></div>

              {/* Categories header */}
              <div className="flex items-center gap-2 mb-3 px-1">
                <span className="w-7 h-7 flex items-center justify-center rounded-md bg-accent-100 text-accent-600">
                  <i className="ri-apps-line text-sm"></i>
                </span>
                <span className="text-sm font-bold text-foreground-800">{t('nav.mobile_categories')}</span>
              </div>

              {/* Categories grid */}
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat, idx) => {
                  const bgColors = [
                    'bg-rose-50 text-rose-600',
                    'bg-amber-50 text-amber-600',
                    'bg-emerald-50 text-emerald-600',
                    'bg-sky-50 text-sky-600',
                    'bg-violet-50 text-violet-600',
                    'bg-orange-50 text-orange-600',
                    'bg-teal-50 text-teal-600',
                    'bg-indigo-50 text-indigo-600',
                    'bg-pink-50 text-pink-600',
                    'bg-cyan-50 text-cyan-600',
                    'bg-lime-50 text-lime-600',
                    'bg-fuchsia-50 text-fuchsia-600',
                    'bg-yellow-50 text-yellow-600',
                    'bg-slate-50 text-slate-600',
                  ];
                  const colorClass = bgColors[idx % bgColors.length];
                  return (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex flex-col items-start gap-2 px-3 py-3 text-sm text-foreground-700 hover:bg-background-100 rounded-xl transition-colors cursor-pointer border border-transparent hover:border-background-200"
                    >
                      <span className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-bold flex-shrink-0 ${colorClass}`}>
                        {cat.id.toString().slice(-2)}
                      </span>
                      <span className="text-xs font-medium leading-tight">{t(cat.nameKey)}</span>
                    </a>
                  );
                })}
              </div>

              {/* Divider */}
              <div className="h-px bg-background-200 my-6"></div>

              {/* CTA buttons */}
              <div className="flex flex-col gap-3">
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center justify-center gap-2 px-5 py-3.5 text-sm font-semibold bg-foreground-900 text-background-50 rounded-full text-center cursor-pointer active:scale-[0.98] transition-transform">
                  {t('nav.cta_spreadsheet')}
                  <i className="ri-arrow-right-up-line text-base"></i>
                </a>
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center justify-center gap-2 px-5 py-3.5 text-sm font-semibold bg-accent-500 text-background-50 rounded-full text-center cursor-pointer active:scale-[0.98] transition-transform">
                  {t('nav.cta_buy')}
                  <i className="ri-arrow-right-up-line text-base"></i>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}